import { useState, useEffect } from 'react'
import { Image, Upload, Heart, MessageCircle, User, Plus, Filter, Gamepad2, Trophy, Tag, FolderOpen, X } from 'lucide-react'
import { screenshotsAPI, authAPI, tournamentsAPI, albumsAPI } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import ScreenshotDetailModal from '@/components/ScreenshotDetailModal'
import AlbumManagement from '@/components/AlbumManagement'

import PageHeader from '@/components/PageHeader'
export default function ScreenshotsPage() {
  const [screenshots, setScreenshots] = useState([])
  const [recentScreenshots, setRecentScreenshots] = useState([])
  const [unalbumedScreenshots, setUnalbumedScreenshots] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [selectedAlbum, setSelectedAlbum] = useState(null)

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    game: '',
    tournament_id: '',
    album_id: '',
    tagged_users: []
  })

  // Detail modal state
  const [selectedScreenshot, setSelectedScreenshot] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)

  // Album management state
  const [albumManagementOpen, setAlbumManagementOpen] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    game: '',
    tournament_id: '',
    album_id: '',
    user_id: '',
    tagged_user_id: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [tournaments, setTournaments] = useState([])
  const [albums, setAlbums] = useState([])
  const [users, setUsers] = useState([])

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          const response = await authAPI.getCurrentUser()
          setCurrentUser(response.data.user)
        }
      } catch (error) {
        console.error('Error fetching current user:', error)
      }
    }
    fetchCurrentUser()
  }, [])

  // Fetch screenshots
  const fetchScreenshots = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await screenshotsAPI.getScreenshots(filters)
      setScreenshots(response.data.screenshots)
    } catch (error) {
      console.error('Error fetching screenshots:', error)
      setError('Failed to load screenshots')
    } finally {
      setLoading(false)
    }
  }

  // Fetch recent screenshots (last 20)
  const fetchRecentScreenshots = async () => {
    try {
      const response = await screenshotsAPI.getScreenshots({ limit: 20 })
      setRecentScreenshots(response.data.screenshots)
    } catch (error) {
      console.error('Error fetching recent screenshots:', error)
    }
  }

  // Fetch unalbumed screenshots
  const fetchUnalbumedScreenshots = async () => {
    try {
      const response = await screenshotsAPI.getScreenshots({ album_id: 'none' })
      setUnalbumedScreenshots(response.data.screenshots)
    } catch (error) {
      console.error('Error fetching unalbumed screenshots:', error)
    }
  }

  useEffect(() => {
    if (selectedAlbum || Object.values(filters).some(f => f)) {
      fetchScreenshots()
    } else {
      setLoading(true)
      Promise.all([fetchRecentScreenshots(), fetchUnalbumedScreenshots()])
        .finally(() => setLoading(false))
    }
  }, [filters, selectedAlbum])

  // Fetch albums
  const fetchAlbums = async () => {
    try {
      const response = await albumsAPI.getAlbums()
      setAlbums(response.data.albums || [])
    } catch (error) {
      console.error('Error fetching albums:', error)
      setAlbums([])
    }
  }

  useEffect(() => {
    fetchAlbums()
  }, [])

  // Fetch tournaments for filter
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const response = await tournamentsAPI.getTournaments()
        setTournaments(response.data.tournaments || [])
      } catch (error) {
        console.error('Error fetching tournaments:', error)
        setTournaments([])
      }
    }
    fetchTournaments()
  }, [])

  // Fetch users for filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await authAPI.getUsers()
        setUsers(response.data.users || [])
      } catch (error) {
        console.error('Error fetching users:', error)
        setUsers([])
      }
    }
    if (currentUser) {
      fetchUsers()
    }
  }, [currentUser])

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size must be less than 10MB')
        return
      }
      setSelectedFile(file)
      setUploadError(null)
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()

    if (!selectedFile) {
      setUploadError('Please select a file')
      return
    }

    try {
      setUploading(true)
      setUploadError(null)

      const formData = new FormData()
      formData.append('screenshot', selectedFile)
      formData.append('title', uploadForm.title)
      if (uploadForm.description) formData.append('description', uploadForm.description)
      if (uploadForm.game) formData.append('game', uploadForm.game)
      if (uploadForm.tournament_id) formData.append('tournament_id', uploadForm.tournament_id)
      if (uploadForm.album_id) formData.append('album_id', uploadForm.album_id)
      if (uploadForm.tagged_users.length > 0) {
        formData.append('tagged_users', JSON.stringify(uploadForm.tagged_users))
      }

      const response = await screenshotsAPI.uploadScreenshot(formData)

      setUploadDialogOpen(false)
      setSelectedFile(null)
      setUploadForm({
        title: '',
        description: '',
        game: '',
        tournament_id: '',
        album_id: '',
        tagged_users: []
      })

      fetchScreenshots()
      fetchRecentScreenshots()
      fetchUnalbumedScreenshots()
      fetchAlbums()
    } catch (error) {
      console.error('Upload error:', error)
      setUploadError(error.response?.data?.error || 'Failed to upload screenshot')
    } finally {
      setUploading(false)
    }
  }

  const handleScreenshotClick = (screenshot) => {
    setSelectedScreenshot(screenshot)
    setDetailModalOpen(true)
  }

  const handleScreenshotUpdate = (updatedScreenshot, previousAlbumId) => {
    setSelectedScreenshot(updatedScreenshot)
    setScreenshots(prev => prev.map(s => s.id === updatedScreenshot.id ? updatedScreenshot : s))
    setRecentScreenshots(prev => prev.map(s => s.id === updatedScreenshot.id ? updatedScreenshot : s))
    setUnalbumedScreenshots(prev =>
      updatedScreenshot.album_id
        ? prev.filter(s => s.id !== updatedScreenshot.id)
        : prev.some(s => s.id === updatedScreenshot.id)
          ? prev.map(s => s.id === updatedScreenshot.id ? updatedScreenshot : s)
          : [...prev, updatedScreenshot]
    )
    if (updatedScreenshot.album_id !== previousAlbumId) {
      fetchAlbums()
    }
  }

  const handleScreenshotDelete = (screenshotId) => {
    setDetailModalOpen(false)
    setScreenshots(screenshots.filter(s => s.id !== screenshotId))
    setRecentScreenshots(recentScreenshots.filter(s => s.id !== screenshotId))
    setUnalbumedScreenshots(unalbumedScreenshots.filter(s => s.id !== screenshotId))
    fetchAlbums()
  }

  const handleAlbumClick = (album) => {
    setSelectedAlbum(album)
    setFilters({ ...filters, album_id: album.id })
  }

  const clearAlbumSelection = () => {
    setSelectedAlbum(null)
    setFilters({ ...filters, album_id: '' })
  }

  const ScreenshotCard = ({ screenshot }) => (
    <Card
      className="overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all cursor-pointer"
      onClick={() => handleScreenshotClick(screenshot)}
    >
      {/* Image */}
      <div className="aspect-video bg-gray-800 relative overflow-hidden">
        <img
          src={screenshotsAPI.getScreenshotUrl(screenshot.file_path)}
          alt={screenshot.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Info */}
      <CardContent className="p-4">
        <h3 className="font-semibold mb-1 line-clamp-1">{screenshot.title}</h3>
        {screenshot.description && (
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
            {screenshot.description}
          </p>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-2 mb-3">
          {screenshot.game && (
            <Badge variant="secondary" className="text-xs">
              <Gamepad2 className="h-3 w-3 mr-1" />
              {screenshot.game}
            </Badge>
          )}
          {screenshot.tournament_name && (
            <Badge variant="secondary" className="text-xs">
              <Trophy className="h-3 w-3 mr-1" />
              {screenshot.tournament_name}
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Heart className={`h-4 w-4 ${screenshot.user_liked ? 'fill-red-500 text-red-500' : ''}`} />
              {screenshot.likes_count}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              {screenshot.comments_count}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <User className="h-4 w-4" />
            {screenshot.uploader_username}
          </span>
        </div>

        {/* Tags */}
        {screenshot.tags && screenshot.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {screenshot.tags.slice(0, 3).map((tag) => (
              <Badge key={tag.id} variant="outline" className="text-xs">
                <Tag className="h-3 w-3 mr-1" />
                {tag.username}
              </Badge>
            ))}
            {screenshot.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{screenshot.tags.length - 3} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader icon={Image} title="Screenshots" />
          <p className="text-muted-foreground mt-1">
            Share and browse epic gaming moments
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
          >
            <Filter className="mr-2 h-4 w-4" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          <Button onClick={() => setAlbumManagementOpen(true)} variant="outline">
            <FolderOpen className="mr-2 h-4 w-4" />
            Manage Albums
          </Button>
          {currentUser && (
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Upload Screenshot
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="filter-game">Game</Label>
                <Input
                  id="filter-game"
                  placeholder="Filter by game..."
                  value={filters.game}
                  onChange={(e) => setFilters({ ...filters, game: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="filter-tournament">Tournament</Label>
                <Select
                  value={filters.tournament_id || undefined}
                  onValueChange={(value) => setFilters({ ...filters, tournament_id: value || '' })}
                >
                  <SelectTrigger id="filter-tournament">
                    <SelectValue placeholder="All tournaments" />
                  </SelectTrigger>
                  <SelectContent>
                    {tournaments.map((tournament) => (
                      <SelectItem key={tournament.id} value={tournament.id.toString()}>
                        {tournament.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-user">Uploader</Label>
                <Select
                  value={filters.user_id || undefined}
                  onValueChange={(value) => setFilters({ ...filters, user_id: value || '' })}
                >
                  <SelectTrigger id="filter-user">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-tagged">Tagged User</Label>
                <Select
                  value={filters.tagged_user_id || undefined}
                  onValueChange={(value) => setFilters({ ...filters, tagged_user_id: value || '' })}
                >
                  <SelectTrigger id="filter-tagged">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({ game: '', tournament_id: '', album_id: '', user_id: '', tagged_user_id: '' })
                    clearAlbumSelection()
                  }}
                  className="mt-6"
                >
                  Clear All Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Album Banner */}
      {selectedAlbum && (
        <Alert className="bg-blue-950/50 border-blue-500">
          <FolderOpen className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              Viewing album: <strong>{selectedAlbum.name}</strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAlbumSelection}
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Recent Screenshots or Filtered Results */}
      {!selectedAlbum && !Object.values(filters).some(f => f) ? (
        <>
          {/* Recent Screenshots */}
          <div>
            <h2 className="text-2xl font-bold mb-4">Recent Screenshots</h2>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading screenshots...</p>
              </div>
            ) : recentScreenshots.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Image className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">No screenshots yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Be the first to share a screenshot from the LAN party!
                  </p>
                  {currentUser && (
                    <Button
                      onClick={() => setUploadDialogOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Upload Screenshot
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {recentScreenshots.slice(0, 12).map((screenshot) => (
                  <ScreenshotCard key={screenshot.id} screenshot={screenshot} />
                ))}
              </div>
            )}
          </div>

          {/* Albums Section */}
          {albums.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <FolderOpen className="h-6 w-6" />
                Albums
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {albums.map((album) => (
                  <Card
                    key={album.id}
                    className="overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all cursor-pointer"
                    onClick={() => handleAlbumClick(album)}
                  >
                    {/* Preview Grid */}
                    <div className="aspect-video bg-gray-900 relative overflow-hidden">
                      {album.preview_screenshots && album.preview_screenshots.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {album.preview_screenshots.slice(0, 4).map((preview, idx) => (
                            <div key={idx} className="relative bg-gray-800">
                              <img
                                src={screenshotsAPI.getScreenshotUrl(preview.file_path)}
                                alt={preview.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                          {/* Fill empty slots with gray boxes */}
                          {[...Array(Math.max(0, 4 - (album.preview_screenshots?.length || 0)))].map((_, idx) => (
                            <div key={`empty-${idx}`} className="bg-gray-800" />
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <FolderOpen className="h-16 w-16 text-gray-600" />
                        </div>
                      )}
                      {/* Overlay with count */}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <div className="text-center">
                          <p className="text-white text-2xl font-bold">{album.screenshot_count}</p>
                          <p className="text-white text-sm">screenshots</p>
                        </div>
                      </div>
                    </div>

                    {/* Album Info */}
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1 line-clamp-1">{album.name}</h3>
                      {album.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {album.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{album.creator_username}</span>
                        <Badge variant="outline" className="text-xs">
                          {album.is_public === 1 ? 'Public' : 'Private'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Screenshots Without Album */}
          {unalbumedScreenshots.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Unsorted Screenshots</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {unalbumedScreenshots.slice(0, 8).map((screenshot) => (
                  <ScreenshotCard key={screenshot.id} screenshot={screenshot} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Filtered Results */
        <div>
          <h2 className="text-2xl font-bold mb-4">
            {selectedAlbum ? `${selectedAlbum.name}` : 'Filtered Results'}
          </h2>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading screenshots...</p>
            </div>
          ) : screenshots.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <Image className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No screenshots found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your filters or browse all screenshots
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {screenshots.map((screenshot) => (
                <ScreenshotCard key={screenshot.id} screenshot={screenshot} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Screenshot</DialogTitle>
            <DialogDescription>
              Share your epic gaming moment with everyone
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpload}>
            <div className="space-y-4">
              {/* File input */}
              <div>
                <Label htmlFor="screenshot">Screenshot *</Label>
                <Input
                  id="screenshot"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  required
                />
                {selectedFile && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Give your screenshot a title..."
                  required
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Describe what's happening in this screenshot..."
                  rows={3}
                />
              </div>

              {/* Game */}
              <div>
                <Label htmlFor="game">Game</Label>
                <Input
                  id="game"
                  value={uploadForm.game}
                  onChange={(e) => setUploadForm({ ...uploadForm, game: e.target.value })}
                  placeholder="e.g., Counter-Strike, Minecraft..."
                />
              </div>

              {/* Tournament */}
              <div>
                <Label htmlFor="tournament">Tournament (Optional)</Label>
                <Select
                  value={uploadForm.tournament_id || undefined}
                  onValueChange={(value) => setUploadForm({ ...uploadForm, tournament_id: value || '' })}
                >
                  <SelectTrigger id="tournament">
                    <SelectValue placeholder="Select tournament (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {tournaments.map((tournament) => (
                      <SelectItem key={tournament.id} value={tournament.id.toString()}>
                        {tournament.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Album */}
              <div>
                <Label htmlFor="album">Album (Optional)</Label>
                <Select
                  value={uploadForm.album_id || undefined}
                  onValueChange={(value) => setUploadForm({ ...uploadForm, album_id: value || '' })}
                >
                  <SelectTrigger id="album">
                    <SelectValue placeholder="Select album (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {albums.map((album) => (
                      <SelectItem key={album.id} value={album.id.toString()}>
                        {album.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tagged Users */}
              <div>
                <Label htmlFor="tagged-users">Tag Users (Optional)</Label>
                <Select
                  value={uploadForm.tagged_users[0] || undefined}
                  onValueChange={(value) => {
                    const newTaggedUsers = value ? [parseInt(value)] : []
                    setUploadForm({ ...uploadForm, tagged_users: newTaggedUsers })
                  }}
                >
                  <SelectTrigger id="tagged-users">
                    <SelectValue placeholder="Tag users in this screenshot..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Who appears in this screenshot?
                </p>
              </div>

              {uploadError && (
                <Alert variant="destructive">
                  <AlertDescription>{uploadError}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Screenshot Detail Modal */}
      {selectedScreenshot && (
        <ScreenshotDetailModal
          screenshot={selectedScreenshot}
          open={detailModalOpen}
          onOpenChange={setDetailModalOpen}
          currentUser={currentUser}
          onUpdate={handleScreenshotUpdate}
          onDelete={handleScreenshotDelete}
        />
      )}

      {/* Album Management Modal */}
      <AlbumManagement
        open={albumManagementOpen}
        onOpenChange={(open) => {
          setAlbumManagementOpen(open)
          if (!open) {
            fetchAlbums()
          }
        }}
        currentUser={currentUser}
      />
    </div>
  )
}
